<template>
    <div class="mt-1">
        <portal :to="`widget-header__${data.WidgetID}`">
            <div class="flex w-full justify-end overflow-hidden items-center">
                <el-select
                    v-model="sortBy"
                    :placeholder="$t('general.sortBy')"
                    size="small"
                    class="select-type"
                >
                    <el-option
                        v-for="option in sortByOptions"
                        :key="option.prop === 'callDuration' ? option.label : `${option.label} - ${option.order}`"
                        :value="option"
                        :label="getSortingLabel(option)"
                    />
                </el-select>
            </div>
        </portal>
        <portal :to="`widget-header__${data.WidgetID}-action`">
            <div class="cursor-pointer hidden lg:block">
                <template v-if="showDropDown">
                    <el-dropdown
                        size="mini"
                        class="px-2 py-1-5 hover:bg-primary-100 rounded"
                        trigger="click"
                    >
                        <span class="flex items-center">
                            <i class="vc-icon-filter text-xl text-primary" />
                        </span>
                        <template #dropdown>
                            <el-dropdown-menu class="grid-layout-dropdown-menu">
                                <el-dropdown-item>
                                    <div class="rounded mt-1 flex flex-col border-2">
                                        <div class="main-bg-light rounded-t border-b-2">
                                            <p class="p-2 text-main-sm font-medium main-text-color">
                                                {{ layoutColumns }}
                                                {{ $t('columns.layout') }}
                                            </p>
                                        </div>
                                        <div class="w-full flex p-2 justify-between main-bg-light">
                                            <i
                                                v-for="index in 15"
                                                :key="index"
                                                :class="{'bg-primary-100': index <= layoutColumns}"
                                                class="icon-square mx-margin--1"
                                                @click="updateGrid(index)"
                                            />
                                        </div>
                                    </div>
                                </el-dropdown-item>
                            </el-dropdown-menu>
                        </template>
                    </el-dropdown>
                </template>
            </div>
        </portal>
        <div>
            <div class="flex pb-6 pt-2 extension-cards justify-center">
                <fade-transition
                    :class="{'grid-container overflow-x-auto': sortedExtensions.length, 'w-full': sortedExtensions.length === 0}"
                    :style="renderColumns"
                    class="flex flex-wrap mb-1"
                    group
                >
                    <div
                        v-for="(extension, index) in sortedExtensions"
                        :key="index"
                        class="px-1 relative extension-cards-container"
                    >
                        <extension-card
                            :key="extension[extensionIdProp]"
                            :extension="extension"
                            :settings="getSettings"
                        />
                    </div>
                    <div
                        v-if="sortedExtensions.length === 0"
                        key="no-data"
                        class="flex flex-col w-full items-center"
                    >
                        <WidgetNoData :widget="data" />
                        <h3 class="text-main-xl">
                            {{ $t('general.noData') }}
                        </h3>
                    </div>
                </fade-transition>
            </div>
        </div>
    </div>
</template>
<script>
import get from 'lodash/get'
import set from 'lodash/set'
import times from 'lodash/times'
import uniqBy from 'lodash/uniqBy'
import orderBy from 'lodash/orderBy'
import partition from 'lodash/partition'
import { Dropdown, DropdownItem, DropdownMenu, Option, Select } from 'element-ui'
import { FadeTransition } from 'vue2-transitions'
import { Statuses } from '@/enum/redesignStatusTypes'
import { realTimeSettings } from '@/enum/defaultWidgetSettings'
import { getInitialExtensionTime, getInitialTime } from '@/util/timeUtils'
import widgetFilters from '@/mixins/widgetFilters'
import { pageWidth } from '@/composables/useBreakpoints'

const cardWidth = 300
const extensionIdProp = 'number'

export default {
    mixins: [ widgetFilters ],
    components: {
        [Select.name]: Select,
        [Option.name]: Option,
        [Dropdown.name]: Dropdown,
        [DropdownMenu.name]: DropdownMenu,
        [DropdownItem.name]: DropdownItem,
        ExtensionCard: () => import('@/components/Cards/ExtensionCard.vue'),
        FadeTransition,
        WidgetNoData: () => import('@/components/Widgets/WidgetNoData.vue')
    },
    props: {
        editable: {
            type: Boolean,
            default: false,
        },
        data: {
            type: Object,
            default: () => ({})
        },
    },
    data () {
        return {
            statusMappings: {
                1: 'IconLogin',
                2: 'IconLogout',
                3: 'IconLunch',
                5: 'IconAdministrative',
                7: 'IconPrivate',
                9: 'IconOther',
                11: 'IconTraining',
                12: 'IconTeamMeeting',
                13: 'IconBrief',
            },
            timer: 0,
            sortByOptions: [
                {
                    label: this.$t('extensions.sort.created.acs'),
                    value: 'number-asc',
                    prop: extensionIdProp,
                    order: 'asc'
                },
                {
                    label: this.$t('extensions.sort.created.desc'),
                    value: 'number-desc',
                    prop: extensionIdProp,
                    order: 'desc'
                },
                {
                    label: this.$t('extensions.sort.userName.asc'),
                    value: 'userName-asc',
                    prop: 'userName',
                    order: 'asc'
                },
                {
                    label: this.$t('extensions.sort.userName.desc'),
                    value: 'userName-desc',
                    prop: 'userName',
                    order: 'desc'
                },
                {
                    label: this.$t('extensions.sort.statusTime.asc'),
                    value: 'statusTime-asc',
                    prop: 'statusTime',
                    order: 'asc'
                },
                {
                    label: this.$t('extensions.sort.statusTime.desc'),
                    value: 'statusTime-desc',
                    prop: 'statusTime',
                    order: 'desc'
                },
                {
                    label: this.$t('extensions.sort.callDuration.asc'),
                    value: 'callDuration-asc',
                    prop: 'callDuration',
                    order: 'asc'
                },
                {
                    label: this.$t('extensions.sort.callDuration.desc'),
                    value: 'callDuration-desc',
                    prop: 'callDuration',
                    order: 'desc'
                },
            ],
            sortBy: {
                label: this.$t('extensions.sort.static'),
                value: 'number-asc',
                prop: extensionIdProp,
                order: 'asc'
            },
            showGridMenu: false,
            // maxLayoutColumns: 15,
            // layoutColumns: 0,
            showDropDown: false,
            parameterName: '{|extension_list|}',
            extensionIdProp
        }
    },
    mounted () {
        this.$nextTick(() => {
            this.showDropDown = true
        })
    },
    computed: {
        layoutColumns: {
            get () {
                return get(this.data.WidgetLayout, 'layoutColumns') || this.maxCardsToDisplay
            },
            set (val) {
                set(this.data.WidgetLayout, 'layoutColumns', val)
            }
        },
        maxCardsToDisplay () {
            return Math.floor(pageWidth.value / (cardWidth - 1))
        },
        extensions () {
            return this.$store.getters['extensions/extensionsWithCallStatus']
        },
        filteredExtensions () {
            const extensions = this.entitiesFilter(this.extensions, extensionIdProp)
            return uniqBy(extensions, extensionIdProp)
        },
        onlineExtensions () {
            const showLoggedOutUsers = get(this.data.WidgetLayout.settings, 'showLoggedOutUsers')
            if (!showLoggedOutUsers) {
                return this.filteredExtensions.filter(e => e.representativeStatus !== Statuses.LOGOUT)
            }
            return this.filteredExtensions
        },
        sortedExtensions () {
            const updatedExtension = this.onlineExtensions.map(ext => {
                ext.statusTime = getInitialExtensionTime(ext, this.getSettings)
                if (ext.calls && ext.calls.length) {
                    const callStarted = (ext.calls.reduce((a, b) => a.callStarted_UTC_CLIENT < b.callStarted_UTC_CLIENT ? a : b)).callStarted_UTC_CLIENT
                    ext.callDuration = getInitialTime(callStarted)
                }
                return ext
            })
            if (this.sortBy == null) {
                return updatedExtension
            }
            if (this.sortBy.prop === 'callDuration') {
                const [ withoutCalls, withCalls ] = partition(updatedExtension, o => !o.calls.length)
                if (this.sortBy.order === 'asc') {
                    return withCalls.sort(function (a, b) {
                        const aVal = a.calls.length > 1 ? a.calls.reduce((acc, val) => (acc.callStarted_UTC_CLIENT < val.callStarted_UTC_CLIENT ? acc.callStarted_UTC_CLIENT : val.callStarted_UTC_CLIENT)) : a.calls[0].callStarted_UTC_CLIENT
                        const bVal = b.calls.length > 1 ? b.calls.reduce((acc, val) => (acc.callStarted_UTC_CLIENT < val.callStarted_UTC_CLIENT ? acc.callStarted_UTC_CLIENT : val.callStarted_UTC_CLIENT)) : b.calls[0].callStarted_UTC_CLIENT
                        if (aVal > bVal) {
                            return 1
                        } else if (aVal < bVal) {
                            return -1
                        } else {
                            return 0
                        }
                    })
                        .concat(
                            orderBy(
                                withoutCalls,
                                'lastAnsweredCallEventEpoch',
                                `${this.sortBy.order}`
                            ))
                } else if (this.sortBy.order === 'desc') {
                    return orderBy(withoutCalls, 'lastAnsweredCallEventEpoch', 'asc')
                        .concat(
                            withCalls.sort(function (a, b) {
                                const aVal = a.calls.length > 1 ? a.calls.reduce((acc, val) => (acc.callStarted_UTC_CLIENT < val.callStarted_UTC_CLIENT ? acc.callStarted_UTC_CLIENT : val.callStarted_UTC_CLIENT)) : a.calls[0].callStarted_UTC_CLIENT
                                const bVal = b.calls.length > 1 ? b.calls.reduce((acc, val) => (acc.callStarted_UTC_CLIENT < val.callStarted_UTC_CLIENT ? acc.callStarted_UTC_CLIENT : val.callStarted_UTC_CLIENT)) : b.calls[0].callStarted_UTC_CLIENT
                                if (aVal < bVal) {
                                    return 1
                                } else if (aVal > bVal) {
                                    return -1
                                } else {
                                    return 0
                                }
                            }))
                }
            }

            const [ unsortable, sortable ] = partition(updatedExtension, o => get(o, this.sortBy.prop) === undefined || o.representativeStatus === Statuses.LOGOUT)

            return orderBy(sortable, `${this.sortBy.prop}`, `${this.sortBy.order}`)
                .concat(unsortable)
        },
        renderColumns () {
            let columns = ''
            times(this.layoutColumns, () => {
                columns = columns + ' auto'
            })
            return {
                'grid-template-columns': columns,
            }
        },
        getSettings () {
            return this.data.WidgetLayout.settings || realTimeSettings
        },
    },
    methods: {
        onMenuClickOutside () {
            this.showGridMenu = false
        },
        updateGrid (val) {
            this.layoutColumns = val
            this.$emit('on-update', this.data)
        },
        getSortingLabel (option) {
            const order = this.$t(`sorting.${option.order}`)
            return `${option.label} - ${order}`
        }
    },
    watch: {
        sortBy (newV) {
            if (newV == null) {
                return
            }
            const { sortingParams } = this.data.WidgetLayout
            if (sortingParams) {
                sortingParams.order = newV.order
                sortingParams.prop = newV.prop
            } else {
                this.data.WidgetLayout.sortingParams = {
                    order: newV.order,
                    prop: newV.prop
                }
            }
            this.$emit('on-update', this.data, true)
        },
        'data.WidgetLayout.sortingParams': {
            immediate: true,
            handler (newV) {
                if (newV && ((this.sortBy.prop !== newV.prop) || (this.sortBy.order !== newV.order))) {
                    this.sortBy = this.sortByOptions.find(data => {
                        // idk but sorting prop set to 'lastAnsweredCallEventEpoch'
                        // TODO: remove this in future(>= march/april 2024)
                        if (data.prop === 'lastAnsweredCallEventEpoch') {
                            return (data.order === newV.order)
                        } else {
                            return (data.prop === newV.prop) && (data.order === newV.order)
                        }
                    })
                }
            }
        }
        //     pageWidth: {
        //         immediate: true,
        //         handler: function (val) {
        //             let numCardsWhichFit = parseInt(val / cardWidth - 1)
        //             this.layoutColumns = numCardsWhichFit
        //             this.maxLayoutColumns = numCardsWhichFit
        //         },
        //     },
    },
}
</script>
<style lang="scss" scoped>
.icon-square {
    width: 20px;
    height: 20px;
    border-radius: 5px;
    border: 2px solid var(--silver-color);

    &:hover {
        background: var(--silver-color);
        border: 2px solid var(--primary-color);
    }
}

.mx-margin--1 {
    margin-right: 1px;
    margin-left: 1px;
}

.grid-container {
    display: grid;
}

.grid-layout-dropdown-menu {
    :deep(.el-dropdown-menu__item) {
        &:focus, &:not(.is_disabled):hover {
            background-color: inherit;
            color: inherit;
        }
    }
}

.extension-cards-container {
    width: 310px;
}
</style>
<style lang="scss">
.flip-list-move {
    transition: transform 5s;
}

.el-row {
    margin-bottom: 20px;

    &:last-child {
        margin-bottom: 0;
    }
}

[dir="ltr"] .select-type {
    @apply mr-2;
}

[dir="rtl"] .select-type {
    @apply ml-2;
}

.extension-cards {

}
</style>
